'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthToken } from '@/hooks/useAuthToken';

export interface CannyDocument {
  id: string;
  name: string;
  status: 'processing' | 'ready' | 'failed';
  chunkCount?: number;
  error?: string | null;
}

const POLL_MS = 2000;
const ACCEPT = '.pdf,.docx,.txt,.md,.csv';

/** Что можно приложить. Проверяем на клиенте, чтобы не гонять зря файл в хранилище. */
export function isAcceptedName(name: string): boolean {
  const ext = name.slice(name.lastIndexOf('.')).toLowerCase();
  return ['.pdf', '.docx', '.txt', '.md', '.csv'].includes(ext);
}

export { ACCEPT as CANNY_ACCEPT };

export function useCannyDocuments(chatId: string | null) {
  const { authedFetch } = useAuthToken();
  const [documents, setDocuments] = useState<CannyDocument[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    const query = chatId ? `?chatId=${encodeURIComponent(chatId)}` : '';
    const response = await authedFetch(`/api/canny/documents${query}`);
    if (!response.ok) return;
    const data = await response.json();
    setDocuments(data.documents ?? []);
  }, [authedFetch, chatId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Опрос идёт, только пока что-то реально обрабатывается. Бесконечный поллинг
  // на открытой вкладке дорого обходится и серверу, и батарее.
  useEffect(() => {
    const pending = documents.some((doc) => doc.status === 'processing');
    if (!pending) {
      if (timer.current) clearTimeout(timer.current);
      return;
    }
    timer.current = setTimeout(() => void refresh(), POLL_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [documents, refresh]);

  const upload = useCallback(
    async (file: File) => {
      setUploadError(null);
      if (!isAcceptedName(file.name)) {
        setUploadError('Такой формат я прочитать не смогу. Подойдут PDF, DOCX и текстовые файлы.');
        return;
      }
      try {
        // Свой presign: тот, что в студии, пропускает только image/*.
        const presignResponse = await authedFetch('/api/canny/documents/presign', {
          method: 'POST',
          body: JSON.stringify({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
          }),
        });
        const presign = await presignResponse.json();
        if (!presignResponse.ok || !presign?.uploadUrl || !presign?.url) {
          throw new Error(presign?.error || 'не удалось подготовить загрузку');
        }
        // Заголовки приходят от бэкенда — он один знает, S3 там или Azure Blob.
        const putHeaders: Record<string, string> =
          presign.headers && Object.keys(presign.headers).length > 0
            ? presign.headers
            : { 'Content-Type': file.type || 'application/octet-stream' };

        const put = await fetch(presign.uploadUrl, {
          method: 'PUT',
          headers: putHeaders,
          body: file,
        });
        if (!put.ok) throw new Error(`не удалось загрузить файл в хранилище (${put.status})`);
        const url = presign.url as string;

        const response = await authedFetch('/api/canny/documents', {
          method: 'POST',
          body: JSON.stringify({
            name: file.name,
            mime: file.type,
            size: file.size,
            url,
            chatId,
          }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || 'не удалось приложить файл');
        setDocuments((current) => [
          { id: data.id, name: data.name, status: data.status },
          ...current,
        ]);
      } catch (error) {
        setUploadError(error instanceof Error ? error.message : 'не удалось приложить файл');
      }
    },
    [authedFetch, chatId],
  );

  const remove = useCallback(
    async (id: string) => {
      await authedFetch(`/api/canny/documents/${id}`, { method: 'DELETE' });
      setDocuments((current) => current.filter((doc) => doc.id !== id));
    },
    [authedFetch],
  );

  return { documents, upload, remove, uploadError, dismissError: () => setUploadError(null) };
}
